import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { portalApi, subjectKey } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { PERMISSIONS } from '@/types/permissions';
import type { ChildRef, PortalStudent, PortalSubject } from '@/types/portal';

export type PortalMode = 'self' | 'guardian' | 'none';

export interface PortalSubjectState {
  /** 'self' for a student, 'guardian' for a parent, 'none' for anybody else. */
  mode: PortalMode;
  /** Null while loading, or for a guardian with no linked children. */
  subject: PortalSubject | null;
  /** The subject's cache key ('me' or a studentId); '' when there is no subject. */
  key: string;
  /** Who the pages are about: the student themselves, or the chosen child. */
  student: PortalStudent | ChildRef | undefined;
  /** A guardian's linked children; empty for a student. */
  children: ChildRef[];
  selectChild: (studentId: string) => void;
  /** Appends `?child=` for a guardian, so moving between portal pages keeps the chosen child. */
  withChild: (path: string) => string;
  isPending: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Whose portal this is.
 *
 * A student (STUDENT_SELF_VIEW) is always 'me'. A guardian (STUDENT_CHILD_VIEW)
 * looks at one child at a time, chosen by `?child=<studentId>` and defaulting to
 * the first child the server lists. The id in the URL is only a choice among
 * the children the server already returned — and the server re-checks the link
 * on every request anyway, answering 404 for a child who is not theirs.
 */
export function usePortalSubject(): PortalSubjectState {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();

  const isSelf = can(PERMISSIONS.STUDENT_SELF_VIEW);
  const isGuardian = !isSelf && can(PERMISSIONS.STUDENT_CHILD_VIEW);
  const mode: PortalMode = isSelf ? 'self' : isGuardian ? 'guardian' : 'none';

  const me = useQuery({
    queryKey: queryKeys.portal.me,
    queryFn: portalApi.me,
    enabled: isSelf,
  });

  const childList = useQuery({
    queryKey: queryKeys.portal.children,
    queryFn: portalApi.children,
    enabled: isGuardian,
  });

  const children = childList.data ?? [];
  const requested = params.get('child');
  const selected = isGuardian
    ? (children.find((child) => child.studentId === requested) ?? children.at(0))
    : undefined;

  const subject: PortalSubject | null = isSelf
    ? 'me'
    : selected !== undefined
      ? { studentId: selected.studentId }
      : null;

  const selectChild = useCallback(
    (studentId: string) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set('child', studentId);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const selectedId = selected?.studentId;
  const withChild = useCallback(
    (path: string) =>
      isGuardian && selectedId !== undefined
        ? `${path}${path.includes('?') ? '&' : '?'}child=${selectedId}`
        : path,
    [isGuardian, selectedId],
  );

  const active = isSelf ? me : childList;

  return {
    mode,
    subject,
    key: subject === null ? '' : subjectKey(subject),
    student: isSelf ? me.data : selected,
    children,
    selectChild,
    withChild,
    isPending: mode !== 'none' && active.isPending,
    error: mode === 'none' ? null : active.error,
    refetch: () => void active.refetch(),
  };
}
